import { Schema } from "effect";
import { RequestIdSchema, WorkspaceIdSchema } from "./ids";
import type {
  ImageGenerationUiProjection,
  ProjectImageGenerationCommand,
} from "./image-generation";
import type { AgentBrowserUiProjection } from "./agent-browser";
import type { ProjectFileMessageReference } from "./project-files";
import type { QuestionnaireAnswer } from "./questionnaire";
import type { ReviewComment } from "./review-comments";
import {
  PapersFlowBoundaryParseOptions,
  IsoDateTimeSchema,
  NonNegativeSafeIntegerSchema,
  toPapersFlowValidationSchema,
} from "./standard";

const ActivityIdentifierSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(180));
const NormalizedProgressSchema = Schema.Finite.check(Schema.isBetween({ minimum: 0, maximum: 1 }));

export const AgentActivityKindSchema = Schema.Literals([
  "run.started",
  "run.status",
  "run.completed",
  "run.failed",
  "run.cancelled",
  "agent.spawned",
  "agent.started",
  "agent.status",
  "agent.completed",
  "agent.failed",
  "agent.cancelled",
  "step.started",
  "step.completed",
  "tool.queued",
  "tool.started",
  "tool.progress",
  "tool.completed",
  "tool.failed",
  "project-task-proposal.ready",
  "approval.requested",
  "approval.decided",
  "file.read",
  "file.changed",
  "terminal.started",
  "terminal.output",
  "terminal.completed",
  "browser.navigated",
  "browser.console",
  "usage.updated",
  "notice",
]);

export const AgentActivityStatusSchema = Schema.Literals([
  "queued",
  "running",
  "waiting",
  "waiting-for-approval",
  "succeeded",
  "failed",
  "cancelled",
]);

export const AgentActivityLevelSchema = Schema.Literals(["debug", "info", "warning", "error"]);

const AgentActivityToolSchema = Schema.Struct({
  name: ActivityIdentifierSchema,
  callId: Schema.optionalKey(ActivityIdentifierSchema),
  risk: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(80))),
});

const AgentActivityTargetSchema = Schema.Struct({
  kind: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  id: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(1_024))),
  label: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(500))),
});

const AgentActivityMetricsSchema = Schema.Struct({
  durationMs: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  inputTokens: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  outputTokens: Schema.optionalKey(NonNegativeSafeIntegerSchema),
});

export const AgentActivityEventSchema = Schema.Struct({
  eventId: ActivityIdentifierSchema,
  sequence: NonNegativeSafeIntegerSchema,
  occurredAt: IsoDateTimeSchema,
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  runId: ActivityIdentifierSchema,
  parentRunId: Schema.optionalKey(ActivityIdentifierSchema),
  agentId: ActivityIdentifierSchema,
  parentAgentId: Schema.optionalKey(ActivityIdentifierSchema),
  agentName: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
  agentRole: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(120))),
  kind: AgentActivityKindSchema,
  status: AgentActivityStatusSchema,
  level: AgentActivityLevelSchema,
  title: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  detail: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(4_000))),
  progress: Schema.optionalKey(NormalizedProgressSchema),
  step: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  tool: Schema.optionalKey(AgentActivityToolSchema),
  target: Schema.optionalKey(AgentActivityTargetSchema),
  metrics: Schema.optionalKey(AgentActivityMetricsSchema),
  /**
   * Persistent events become message data parts and can be replayed with the thread.
   * Transient events are delivered only to the active client through `onData`.
   */
  persistence: Schema.Literals(["persistent", "transient"]),
});

export const SubagentSnapshotSchema = Schema.Struct({
  agentId: ActivityIdentifierSchema,
  parentAgentId: Schema.optionalKey(ActivityIdentifierSchema),
  name: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
  role: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(120))),
  status: AgentActivityStatusSchema,
  currentTitle: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(240))),
  progress: Schema.optionalKey(NormalizedProgressSchema),
  startedAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  completedAt: Schema.optionalKey(IsoDateTimeSchema),
});

export const AgentActivitySnapshotSchema = Schema.Struct({
  runId: ActivityIdentifierSchema,
  status: AgentActivityStatusSchema,
  startedAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  completedAt: Schema.optionalKey(IsoDateTimeSchema),
  activeAgentCount: NonNegativeSafeIntegerSchema,
  completedAgentCount: NonNegativeSafeIntegerSchema,
  failedAgentCount: NonNegativeSafeIntegerSchema,
  agents: Schema.Array(SubagentSnapshotSchema).check(Schema.isMaxLength(64)),
});

export const AgentNotificationSchema = Schema.Struct({
  level: Schema.Literals(["info", "warning", "error"]),
  message: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(2_000)),
});

export const AgentActivityEventStandardSchema =
  toPapersFlowValidationSchema(AgentActivityEventSchema);
export const AgentActivitySnapshotStandardSchema = toPapersFlowValidationSchema(
  AgentActivitySnapshotSchema,
);
export const AgentNotificationStandardSchema =
  toPapersFlowValidationSchema(AgentNotificationSchema);

export const decodeAgentActivityEvent = Schema.decodeUnknownSync(
  AgentActivityEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeAgentActivityEvent = Schema.encodeSync(
  AgentActivityEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const areAgentActivityEventsEquivalent = Schema.toEquivalence(AgentActivityEventSchema);
export const decodeAgentActivityEventOption = Schema.decodeUnknownOption(
  AgentActivityEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentActivitySnapshotOption = Schema.decodeUnknownOption(
  AgentActivitySnapshotSchema,
  PapersFlowBoundaryParseOptions,
);

export type AgentActivityKind = typeof AgentActivityKindSchema.Type;
export type AgentActivityStatus = typeof AgentActivityStatusSchema.Type;
export type AgentActivityEvent = typeof AgentActivityEventSchema.Type;
export type AgentActivityEventEncoded = typeof AgentActivityEventSchema.Encoded;
export type AgentActivitySnapshot = typeof AgentActivitySnapshotSchema.Type;
export type AgentActivitySnapshotEncoded = typeof AgentActivitySnapshotSchema.Encoded;
export type AgentNotification = typeof AgentNotificationSchema.Type;
export type AgentNotificationEncoded = typeof AgentNotificationSchema.Encoded;

/** Data-part names used by AI SDK UI's custom message protocol. */
export type WorkspaceAgentDataParts = {
  readonly "agent-event": AgentActivityEvent;
  readonly "agent-snapshot": AgentActivitySnapshot;
  readonly "agent-notification": AgentNotification;
  readonly "agent-browser": AgentBrowserUiProjection;
  readonly "project-file": ProjectFileMessageReference;
  readonly "questionnaire-answer": QuestionnaireAnswer;
  readonly "review-comment": ReviewComment;
  readonly "image-generation": ImageGenerationUiProjection;
  readonly "image-generation-request": ProjectImageGenerationCommand;
};
